<template>
  <div>
    <template v-for="(block, index) in renderedBlocks">
      <!--
        Blocks whose content is a visually-bulleted list get rendered as a real list so the
        markup carries list semantics (WCAG 1.3.1). Each item is still rendered by
        wiki-text-block, so strategy/resource popovers inside an item keep working.

        A segmented block is never itself locked or fully locked (see renderedBlocks), but it
        can still be in lock_links mode, so every child gets locked-links — otherwise a link
        inside a rebuilt list would become clickable for a user whose links are locked.
      -->
      <template v-if="block.segments">
        <template v-for="(segment, segmentIndex) in block.segments">
          <ul
            v-if="segment.type === 'list'"
            class="wiki-text-list"
            :key="`${index}-${segmentIndex}`">
            <li v-for="(item, itemIndex) in segment.items" :key="itemIndex">
              <wiki-text-block
                :text="item.wikiText"
                :plaintext="plaintext"
                :popover-placement="popoverPlacement"
                :resources="resources"
                :strategies="strategies"
                :markdown-only="item.markdownOnly && !plaintext"
                :locked-links="lockedLinkWikiBlock" />
            </li>
          </ul>
          <wiki-text-block
            v-else
            :text="segment.wikiText"
            :plaintext="plaintext"
            :popover-placement="popoverPlacement"
            :resources="resources"
            :strategies="strategies"
            :markdown-only="segment.markdownOnly && !plaintext"
            :locked-links="lockedLinkWikiBlock"
            :key="`${index}-${segmentIndex}`" />
        </template>
      </template>
      <wiki-text-block
        v-else
        :text="block.wikiText"
        :plaintext="plaintext"
        :popover-placement="popoverPlacement"
        :resources="resources"
        :strategies="strategies"
        :markdown-only="block.markdownOnly && !plaintext"
        :key="index"
        :locked-links="lockedLinkWikiBlock"
        :locked="block.locked"
        :locked-full="block.lockedFull" />
    </template>
    <div v-if="hasMultilingualResources && objectId">
      <ResourceLanguageDropdown :object-id="objectId" />
    </div>
  </div>
</template>

<script>
import { breakdownWikiTextBlocks, segmentVisualLists } from '@/util/tokenize_wiki_text'
import WikiTextBlock from '@/components/WikiTextBlock'
import { mapGetters } from 'vuex'
import ResourceLanguageDropdown from '@/components/ResourceLanguageDropdown'

let lockModes = ['default', 'lock_links', 'always_snippet_first_block', 'completely_blur'] // 'default' mode is if there's only one block, we partially blur. If there's more than one block, we only blur blocks after the first one. Other modes are relatively self-explanatory.

export default {
  name: 'WikiText',
  components: {
    WikiTextBlock,
    ResourceLanguageDropdown
  },
  props: {
    text: {
      type: String,
      default: ''
    },
    strategies: {
      type: Object,
      default() {
        return {}
      }
    },
    resources: {
      type: Object,
      default() {
        return {}
      }
    },
    plaintext: {
      type: Boolean,
      default: false
    },
    popoverPlacement: {
      type: String,
      default: 'right'
    },
    locked: {
      type: Boolean,
      default: false
    },
    lockMode: {
      type: String,
      default: 'default',
      validator: mode => lockModes.indexOf(mode) > -1
    },
    objectId: {
      type: String,
      default: ''
    },
  },
  computed: {
    wikiTextBlocks() {
      return breakdownWikiTextBlocks(this.text)
    },
    // one entry per wiki text block, in the same order — the block indexes the lock/blur
    // rules depend on are unchanged. `segments` is null unless the block is an unlocked
    // visually-bulleted list, in which case it carries the pieces to render as a real list.
    renderedBlocks() {
      return this.wikiTextBlocks.map((block, index) => {
        let locked = this.lockWikiBlock(index)
        let lockedFull = this.fullyLockWikiBlock(block, index)
        return {
          ...block,
          locked,
          lockedFull,
          // locked blocks show a blurred teaser built from the whole block, so leave them be
          segments: locked || lockedFull ? null : segmentVisualLists(block.wikiText)
        }
      })
    },
    lockedLinkWikiBlock() {
      return this.locked && this.lockMode === 'lock_links'
    },
    ...mapGetters('goaldetail', ['hasNonEnglishLangById']),
    hasMultilingualResources() {
      if (!this.objectId) return false
      return this.hasNonEnglishLangById(this.objectId)
    }
  },
  methods: {
    lockWikiBlock(index) {
      // special case: Handling the first block if there are multiple blocks
      if (this.wikiTextBlocks.length > 1 && index === 0) {
        return (
          this.locked &&
          ['completely_blur', 'always_snippet_first_block'].indexOf(this.lockMode) > -1
        )
      }
      return this.locked && this.lockMode !== 'lock_links'
    },
    fullyLockWikiBlock(block, index) {
      // fully lock (i.e. no visible snippet) applies if we have more than 1 block OR if there's only a single block and it's just an image
      return (
        this.locked &&
        ((this.wikiTextBlocks.length > 1 && index > 0) ||
          this.isSingleMarkdownImage(block) ||
          this.lockMode === 'completely_blur')
      )
    },
    isSingleMarkdownImage(block) {
      return block.markdownOnly && block.wikiText && block.wikiText.split(' ').length === 1
    }
  }
}
</script>

<style>
.wiki-text-container img {
  max-width: 100%;
}
/* lists rebuilt from bullet-glyph content: keep them as tight as the <br>-separated
   lines they replace, and let the list marker stand in for the literal bullet */
.wiki-text-list {
  padding-left: 20px;
  margin-bottom: 10px;
}
.wiki-text-list p {
  margin-bottom: 0;
}
.wiki-text-token {
  display: inline;
}
.highlight {
  display: inline-block;
  background: #ffff84;
  padding: 0 4px;
  margin: 1px 0;
}
.highlight.highlight--resource {
  background: #ffd8fc;
}
.language {
  text-align: right;
}
</style>
